import React, { useCallback, useState } from "react"
import MenuIcon from "@mui/icons-material/Menu"
import DarkModeIcon from "@mui/icons-material/DarkMode"
import LightModeIcon from "@mui/icons-material/LightMode"
import { Box, Button, Divider, Drawer, Stack, Toolbar, Tooltip } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import AppBar from "@mui/material/AppBar"
import { Link } from "react-router-dom"
import { useAppTheme } from "@/shared/context/ThemeContext"
import SitemarkIcon from "@/features/landing/template/components/SitemarkIcon"

const NAV_LINKS = [
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Features", href: "/#features" },
  { label: "FAQ", href: "/#faq" },
  { label: "Pricing", href: "/#pricing" }
]

const UnauthenticatedAppBar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const { mode, toggleTheme } = useAppTheme()

  const handleDrawerClose = useCallback(() => {
    setIsClosing(true)
    setMobileOpen(false)
  }, [])

  const handleDrawerTransitionEnd = useCallback(() => {
    setIsClosing(false)
  }, [])

  const handleDrawerToggle = useCallback(() => {
    if (!isClosing) {
      setMobileOpen(old => !old)
    }
  }, [isClosing])

  return (
    <>
      <AppBar
        sx={theme => ({
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.6)",
          backgroundImage: "none",
          color: theme.palette.text.primary,
          boxShadow: "none"
        })}>
        <Toolbar
          sx={{
            maxWidth: 1200,
            mx: "auto",
            width: "100%",
            px: { xs: 1, sm: 2, md: 4 }
          }}>
          <Stack direction="row" alignItems="center" flex={1}>
            <Stack gap={1} direction="row" alignItems="center" flex={1}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                sx={{ display: { md: "none" } }}
                onClick={handleDrawerToggle}>
                <MenuIcon color="action" />
              </IconButton>
              <Button component={Link} to={"/"}>
                <SitemarkIcon />
              </Button>
            </Stack>
            <Stack direction="row" alignItems="center" gap={1} sx={{ display: { xs: "none", md: "flex" } }}>
              {NAV_LINKS.map(link => (
                <Button to={link.href} component={Link} href={link.href} color="inherit" sx={{ fontWeight: 600 }}>
                  {link.label}
                </Button>
              ))}
            </Stack>
            <Tooltip title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
              <IconButton onClick={toggleTheme} color="inherit" aria-label="toggle theme">
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: "min(320px, 85vw)"
            }
          }}>
          <Box>
            <Toolbar>
              <Link to="/" style={{ textDecoration: "none" }}>
                <Stack direction="row" spacing={1}>
                  <SitemarkIcon />
                </Stack>
              </Link>
            </Toolbar>
            <Divider />
            <Stack sx={{ p: 2 }}>
              {NAV_LINKS.map(link => (
                <Button
                  key={link.href}
                  component="a"
                  href={link.href}
                  color="inherit"
                  sx={{ fontWeight: 600, justifyContent: "flex-start" }}
                  onClick={handleDrawerClose}>
                  {link.label}
                </Button>
              ))}
            </Stack>
          </Box>
        </Drawer>
      </Box>
    </>
  )
}

export default UnauthenticatedAppBar
